import TenantQualifications from "shared/icons/tenantQualifications.svg?react";
import { cx } from "shared/utils";
import { ApplicantType, ApplicationStatus } from "shared/types";
import { VerificationSchemaView } from "../../slices/types";
import { LeftPane } from "./LeftPane";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Hide,
  Show,
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components";
import {
  Award03,
  CurrencyDollar,
  FileCheck02,
  Grid01,
  Mail02,
  Settings02
} from "untitledui-js-base";
import { useTemplateContext } from "../../provider";
import { FC, useEffect, useRef, useState } from "react";
import { Template } from "../../../../../types/landlord/verificationTemplate";
import { TemplateMenu } from "../TemplateMenu";
import { screeningServicesTabs } from "../../constant";
import {
  ArrowLeft02Icon,
  DiscountTag02Icon,
  SidebarLeftIcon,
  SidebarRightIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { ChangeTemplateSubmenu } from "../ChangeTemplateSubmenu";
import { useAuthContext } from "providers/Auth";
import { useNavigate } from "react-router-dom";
import { RefObject } from "react";
import { PawPrintIcon } from "shared/icons/hugeicons-corrected/icons/paw-print-stroke";

interface EditableNameProps {
  readOnly: boolean;
  error?: string;
  editedName: string;
  setEditedName?: (x: string) => void;
  clearError?: () => void;
  toggleEditMode?: () => void;
  inputRef: RefObject<HTMLInputElement>;
}

const EditableName: FC<EditableNameProps> = ({
  readOnly,
  error,
  editedName,
  setEditedName,
  clearError,
  toggleEditMode,
  inputRef
}) => {
  if (readOnly)
    return (
      <h1 className="font-bold text-content-1000 text-lg truncate max-w-[175px]">
        {editedName}
      </h1>
    );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!setEditedName) return;
    setEditedName(e.target.value);
    clearError && clearError();
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (toggleEditMode) toggleEditMode();
    const input = e.target;
    const length = input.value.length;
    requestAnimationFrame(() => {
      input.setSelectionRange(length, length);
    });
  };

  const handleBlur = () => {
    if (toggleEditMode) toggleEditMode();
  };

  return (
    <div className="flex w-full items-center">
      <div className="flex w-full items-center">
        <div className="relative min-w-[1em] w-min flex items-center">
          <span className="invisible whitespace-pre text-lg font-bold text-content-1000 h-10 max-w-[130px] overflow-hidden text-ellipsis">
            {editedName || "Untitled"}
          </span>
          <input
            ref={inputRef}
            className={cx(
              "absolute left-0 w-full text-lg border-none p-0 font-bold focus:outline-none focus:ring-0 max-w-[130px] overflow-hidden text-ellipsis",
              error
                ? "text-danger-600 placeholder:text-danger-600"
                : "text-content-1000 hover:text-content-400 focus:hover:text-content-1000"
            )}
            onChange={handleChange}
            value={editedName}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder="Untitled"
            readOnly={readOnly}
          />
        </div>
      </div>
    </div>
  );
};

interface Props {
  isModal?: boolean;
  error?: string;
  clearError?: () => void;
  isEditing?: boolean;
  template?: Template;
  setEditedName?: (x: string) => void;
  editedName: string;
  toggleEditMode?: () => void;
  handleDuplicate?: () => void;
  setShowDeleteModal?: (v: boolean) => void;
  applicationId?: string;
  applicationStatus?: ApplicationStatus;
  showBackButton?: boolean;
  backLink?: string;
  propertyId?: number;
}

export const SideBar: FC<Props> = ({
  isModal,
  error,
  clearError,
  template,
  editedName,
  setEditedName,
  toggleEditMode,
  handleDuplicate,
  setShowDeleteModal,
  applicationId,
  applicationStatus,
  showBackButton,
  backLink = "/verification-templates",
  propertyId
}) => {
  const navigate = useNavigate();
  const {
    readOnly,
    createMode,
    partnerType,
    setPartnerType,
    setSelectedVerificationSchema,
    selectedVerificationSchema
  } = useTemplateContext();
  const {
    type: { hasTemplateFeeCollection }
  } = useAuthContext();
  const editInputRef = useRef<HTMLInputElement>(null);
  const [overlayOpen, setOverlayOpen] = useState(false);

  const selectTab = (tabName: ApplicantType) => {
    setSelectedVerificationSchema(VerificationSchemaView.ScreeningServices);
    setPartnerType(tabName);
    setOverlayOpen(false);
  };

  const selectVerificationSchema = (v: VerificationSchemaView) => {
    setSelectedVerificationSchema(v);
    setOverlayOpen(false);
  };

  useEffect(() => {
    if (error && editInputRef.current) editInputRef.current.focus();
  }, [error]);

  return (
    <>
      <div className="vt-sidebar-strip">
        <IconButton
          size={IconButtonSize.SM}
          variant={IconButtonVariant.BASE}
          borderLess
          HugeIcon={SidebarLeftIcon}
          onClick={() => setOverlayOpen(true)}
        />
      </div>
      <Show if={overlayOpen}>
        <div
          onClick={() => setOverlayOpen(false)}
          className={cx(
            "vt-sidebar-backdrop",
            isModal && "vt-sidebar-backdrop--modal"
          )}
        />
      </Show>
      <div
        className={cx(
          "vt-sidebar hide-scroll",
          overlayOpen && "vt-sidebar--open",
          isModal && "vt-sidebar--modal"
        )}
      >
        <Show if={showBackButton}>
          <div>
            <Button
              variant={ButtonVariant.BASE}
              size={ButtonSize.XS}
              HugeIcon={ArrowLeft02Icon}
              onClick={() => navigate(backLink)}
              className="!gap-2 !text-content-1000"
            >
              Templates
            </Button>
          </div>
          <div className="my-4 border-b border-alpha-40" />
        </Show>
        <div className="flex justify-between items-center py-2">
          <div className="space-y-2">
            <p
              className={cx(
                "text-sm",
                error ? "text-danger-600" : "text-content-400"
              )}
            >
              Screening template
            </p>
            <EditableName
              readOnly={readOnly}
              error={error}
              editedName={editedName}
              setEditedName={setEditedName}
              clearError={clearError}
              toggleEditMode={toggleEditMode}
              inputRef={editInputRef}
            />
          </div>

          <div className="flex items-center gap-2">
            <Hide if={createMode}>
              <TemplateMenu
                template={template}
                onDelete={
                  setShowDeleteModal
                    ? () => setShowDeleteModal(true)
                    : undefined
                }
                onDuplicate={handleDuplicate}
              />
            </Hide>
            <div className="vt-sidebar-close">
              <IconButton
                size={IconButtonSize.SM}
                variant={IconButtonVariant.BASE}
                HugeIcon={SidebarRightIcon}
                onClick={() => setOverlayOpen(false)}
              />
            </div>
          </div>
        </div>

        <Hide if={!applicationId}>
          <ChangeTemplateSubmenu
            applicationId={applicationId || ""}
            applicationStatus={applicationStatus}
            currentTemplate={template || undefined}
            propertyId={propertyId}
          />
        </Hide>

        <div className="my-4 border-b border-alpha-40" />
        <div className="space-y-1 max-h-[calc(100vh-220px)] overflow-y-auto">
          <LeftPane
            onClick={(v) => selectTab(v as ApplicantType)}
            tabName={VerificationSchemaView.ScreeningServices}
            active={screeningServicesTabs.includes(selectedVerificationSchema)}
            selectedTab={selectedVerificationSchema}
            selectedChild={partnerType}
            Icon={<Grid01 size="20" className="stroke-content-500" />}
            childItems={[
              ApplicantType.Applicant,
              ApplicantType.CoSigner,
              ApplicantType.Occupant
            ]}
          />
          <LeftPane
            onClick={() =>
              selectVerificationSchema(VerificationSchemaView.DecisionCriteria)
            }
            tabName={VerificationSchemaView.DecisionCriteria}
            selectedTab={selectedVerificationSchema}
            Icon={
              <Award03
                size="20"
                className={cx(
                  "stroke-content-500",
                  selectedVerificationSchema ===
                    VerificationSchemaView.DecisionCriteria &&
                    "stroke-primary-500 text-primary-600"
                )}
              />
            }
            disabled={createMode}
            disabledMessage="To configure screening criteria, first create a template"
          />
          <LeftPane
            onClick={() =>
              selectVerificationSchema(
                VerificationSchemaView.TenantQualifications
              )
            }
            tabName={VerificationSchemaView.TenantQualifications}
            selectedTab={selectedVerificationSchema}
            Icon={<TenantQualifications />}
          />
          <LeftPane
            onClick={() =>
              selectVerificationSchema(
                VerificationSchemaView.SubmissionDisclosure
              )
            }
            tabName={VerificationSchemaView.SubmissionDisclosure}
            selectedTab={selectedVerificationSchema}
            Icon={
              <FileCheck02
                size="20"
                className={cx(
                  "stroke-content-500",
                  selectedVerificationSchema ===
                    VerificationSchemaView.SubmissionDisclosure &&
                    "stroke-primary-500 text-primary-600"
                )}
              />
            }
          />
          <LeftPane
            onClick={() =>
              selectVerificationSchema(VerificationSchemaView.DiscountCodes)
            }
            tabName={VerificationSchemaView.DiscountCodes}
            selectedTab={selectedVerificationSchema}
            Icon={
              <HugeiconsIcon
                icon={DiscountTag02Icon}
                size={20}
                className={cx(
                  "stroke-content-500",
                  selectedVerificationSchema ===
                    VerificationSchemaView.DiscountCodes &&
                    "stroke-primary-500 text-primary-600"
                )}
              />
            }
          />
          <Show if={hasTemplateFeeCollection}>
            <LeftPane
              onClick={() =>
                selectVerificationSchema(VerificationSchemaView.AdditionalFees)
              }
              tabName={VerificationSchemaView.AdditionalFees}
              selectedTab={selectedVerificationSchema}
              Icon={
                <CurrencyDollar
                  size="20"
                  className={cx(
                    "stroke-content-500",
                    selectedVerificationSchema ===
                      VerificationSchemaView.AdditionalFees &&
                      "stroke-primary-500 text-primary-600"
                  )}
                />
              }
            />
          </Show>
          <LeftPane
            onClick={() =>
              selectVerificationSchema(VerificationSchemaView.AdverseAction)
            }
            tabName={VerificationSchemaView.AdverseAction}
            selectedTab={selectedVerificationSchema}
            Icon={
              <Mail02
                size="20"
                className={cx(
                  "stroke-content-500",
                  selectedVerificationSchema ===
                    VerificationSchemaView.AdverseAction &&
                    "stroke-primary-500 text-primary-600"
                )}
              />
            }
          />
          <LeftPane
            onClick={() =>
              selectVerificationSchema(VerificationSchemaView.PetPolicy)
            }
            tabName={VerificationSchemaView.PetPolicy}
            selectedTab={selectedVerificationSchema}
            Icon={
              <HugeiconsIcon
                size="20"
                icon={PawPrintIcon}
                className={cx(
                  "stroke-content-500",
                  selectedVerificationSchema ===
                    VerificationSchemaView.PetPolicy &&
                    "stroke-primary-500 text-primary-600"
                )}
              />
            }
            newUntilDate="2026-10-15"
          />
          <LeftPane
            onClick={() =>
              selectVerificationSchema(VerificationSchemaView.General)
            }
            tabName={VerificationSchemaView.General}
            selectedTab={selectedVerificationSchema}
            Icon={
              <Settings02
                size="20"
                className={cx(
                  "stroke-content-500",
                  selectedVerificationSchema ===
                    VerificationSchemaView.General &&
                    "stroke-primary-500 text-primary-600"
                )}
              />
            }
          />
        </div>
      </div>
    </>
  );
};
